import { path } from 'ramda'

import { isPromise } from '@/lib/helpers'
import { DEVICE_CLASSES } from './consts'
import DeviceIdentity from './device-identity'
import type { ApiCallParams, DeviceIdentityLogger, NativeIdentityConstructorParams, TokenAction } from '../types'

type DeviceClassConstructor = new (config: { apiCall: (params: ApiCallParams) => Promise<unknown>; logger: DeviceIdentityLogger }) => DeviceIdentity

export default class NativeIdentity {
  __apiCall: (params: ApiCallParams) => Promise<unknown>
  __getToken: () => Promise<string | null | undefined | unknown>
  __logger: DeviceIdentityLogger
  __deviceIdentities!: DeviceIdentity[]

  constructor ({ apiCall, getToken, logger }: NativeIdentityConstructorParams) {
    this.__apiCall = apiCall
    this.__getToken = getToken
    this.__logger = logger
    this.__addDeviceIdentities()
  }

  __addDeviceIdentities (): void {
    const classes = Object.values(DEVICE_CLASSES)
    this.__deviceIdentities = classes.map(this.__addDeviceIdentity.bind(this)) as DeviceIdentity[]
  }

  __addDeviceIdentity (DeviceClass: unknown): DeviceIdentity | undefined {
    const isValid = DeviceClass && (DeviceClass as typeof DeviceIdentity).prototype instanceof DeviceIdentity

    if (isValid) {
      return new (DeviceClass as DeviceClassConstructor)({
        apiCall: this.__apiCall,
        logger: this.__logger,
      })
    }

    this.__logger.error('Object doesn\'t has DeviceIdentity as base class')
    return undefined
  }

  async tokenAction ({ action, business, webView }: { action: TokenAction; business: Record<string, unknown>; webView: string }): Promise<unknown> {
    const data = { action, business, webView }

    this.__logger.log('Token action started', data)

    const identity = this.__getDeviceIdentity({ webView })
    const businessId = path(['id'], business)

    try {
      this.__validateTokenAction({ action, businessId, identity })

      const token = await this.__tryToGetToken()
      const result = (identity as DeviceIdentity & Record<string, (p: { businessId: unknown; token: unknown }) => Promise<unknown>>)[action]({ businessId, token }) as unknown as Promise<unknown>

      if (isPromise(result)) {
        return result.finally(() => {
          this.__logger.log('Token action settled', data)
        })
      }

      throw new Error('Token action result is not a promise')
    } catch (error) {
      this.__logger.error(error, data)
      return Promise.reject(error)
    }
  }

  __getDeviceIdentity ({ webView }: { webView: string }): DeviceIdentity | undefined {
    return this.__deviceIdentities.find(deviceIdentity => {
      const DeviceClass = DEVICE_CLASSES[webView]
      return DeviceClass && deviceIdentity instanceof DeviceClass
    })
  }

  __validateTokenAction ({ action, businessId, identity }: { action: TokenAction; businessId: unknown; identity: DeviceIdentity | undefined }): void {
    if (!identity) {
      throw new Error('Identity is not defined')
    }

    if (!businessId) {
      throw new Error('businessId is not defined')
    }

    if (typeof identity[action] !== 'function') {
      throw new Error('Action must be a function')
    }
  }

  async __tryToGetToken (): Promise<string | Record<string, unknown>> {
    try {
      const token = await this.__getToken()

      if (!token) {
        throw new Error('Token is not defined')
      }

      return token as string | Record<string, unknown>
    } catch (error) {
      throw new Error(String(error))
    }
  }
}
